/* eslint-disable @typescript-eslint/no-explicit-any */
import { Reducer, AnyAction } from 'redux'
import { createSelector, PayloadAction } from '@reduxjs/toolkit'
import { Filter, keplerGlReducer as _keplerGlReducer } from '@carto/kepler.gl/reducers'
import { ActionTypes, LayerConfigChangeUpdaterAction } from '@carto/kepler.gl/actions'
import { Layer } from '@carto/kepler.gl/layers'
// @ts-expect-error TODO: Improve types.
import { UnconnectedKeplerGlProps } from '@carto/kepler.gl/components'
import TileLayer from '../ui/KeplerGl/layers/TileLayer'
import QuadbinLayer from '../ui/KeplerGl/layers/QuadbinLayer'
import HeatmapTileLayer from '../ui/KeplerGl/layers/HeatmapTileLayer'
import ClusterTileLayer from '../ui/KeplerGl/layers/ClusterTileLayer'
import { MapType } from 'features/maps/types'
import { updateAllLayerDomainData } from '@carto/kepler.gl/utils'
import { TilejsonResult } from '@carto/api-client'
import {
  getAttributesFromTilesetDataset,
  getFieldsTypesFromTilesetDataset,
  getLayerOrderWithMove,
  getZoomRangeFromTilesetDataset,
  setAggregationResLevelFromTilesetDataset,
  setSchemaFromTilesetDataset,
  setTilejsonFromTilesetDataset
} from './builderSliceUtils'
import { RootState } from 'app/state/store'
import { LAYER_SIDE_PANEL } from '../ui/builderConstants'
import H3Layer from '../ui/KeplerGl/layers/H3Layer'
import {
  setIsLayerLoadingTiles,
  updateTilesetDatasetAttributeStats,
  updateTilesetDatasetResolution,
  updateTilesetDatasetTilejson,
  updateTilesetHexColumnStats,
  reorderLayerWithMove
} from './keplerGlActions'
import { RequestStatus } from 'features/common'
import UnknownLayer from '../ui/KeplerGl/layers/UnknownLayer'
import {
  AttributeStats,
  KeplerTable,
  KeplerLayerClasses,
  LayerTypes,
  KeplerLayerConfig,
  LayerMove,
  KeplerLayer
} from '../types'
import { SchemaField } from 'features/data-explorer/types'
import RasterTileLayer from '../ui/KeplerGl/layers/RasterTileLayer'
import { createEntitySelector } from 'app/state/selectorUtils'

const keplerGlReducer: Reducer<{ map: typeof UnconnectedKeplerGlProps }, AnyAction> = _keplerGlReducer
  .initialState({
    uiState: {
      activeSidePanel: '',
      currentModal: null,
      mapControls: {
        visibleLayers: {
          show: true
        },
        mapLegend: {
          show: true
        },
        toggle3d: {
          show: false
        },
        splitMap: {
          show: false
        },
        mapDraw: {
          show: false
        },
        mapLocale: {
          show: false
        }
      }
    },
    visState: {
      layerClasses: getLayerClasses()
    },
    mapStyle: {
      mapStyles: {}
    }
  })
  .plugin({
    [ActionTypes.LAYER_CONFIG_CHANGE]: (
      state: typeof UnconnectedKeplerGlProps,
      action: LayerConfigChangeUpdaterAction
    ) => {
      const { oldLayer } = action

      if (action.newConfig && action.newConfig.isConfigActive) {
        state.visState.layers.forEach((l: Layer) => {
          if (l.id !== oldLayer.id) {
            l.config.isConfigActive = false
          }
        })
      }

      return state
    },
    // Deck-gl rename lngLat to coordinate in pickInfo in 6.3.0 version so this action is necessary to place the popup in the correct place when a layer is clicked
    [ActionTypes.LAYER_CLICK]: (
      state: typeof UnconnectedKeplerGlProps,
      action: { info: { picked: boolean; coordinate: Array<number> } }
    ) => {
      if (action?.info?.picked && action?.info?.coordinate) {
        state.visState.clicked.lngLat = action.info.coordinate
      }
      return state
    },
    [updateTilesetDatasetAttributeStats.type]: (
      state: typeof UnconnectedKeplerGlProps,
      action: PayloadAction<{ datasetId: string; stats: AttributeStats }>
    ) => {
      const { datasetId, stats } = action.payload
      const dataset: KeplerTable | undefined = state.visState.datasets[datasetId]
      const fields = dataset && getFieldsTypesFromTilesetDataset(dataset)

      if (dataset && fields) {
        const attributes = getAttributesFromTilesetDataset(dataset)
        const currentAttributeIndx = attributes.findIndex((attr) => attr.attribute === stats.attribute)

        if (currentAttributeIndx >= 0) {
          attributes.splice(currentAttributeIndx, 1)
        }
        attributes.push({
          ...stats,
          type: fields[stats.attribute]
        } as AttributeStats)
      }
      return updateVisStateDatasets(state, datasetId)
    },
    [updateTilesetHexColumnStats.type]: (
      state: typeof UnconnectedKeplerGlProps,
      action: PayloadAction<{ datasetId: string; stats: AttributeStats }>
    ) => {
      const { datasetId, stats } = action.payload
      const dataset: KeplerTable | undefined = state.visState.datasets[datasetId]

      if (dataset) {
        dataset.metadata.hexColumnStats = {
          ...dataset.metadata.hexColumnStats,
          [stats.attribute]: stats
        }
      }
      return updateVisStateDatasets(state, datasetId)
    },
    [setIsLayerLoadingTiles.type]: (
      state: typeof UnconnectedKeplerGlProps,
      action: PayloadAction<{ layerId: string; isLoadingTiles: boolean }>
    ) => {
      const { layerId, isLoadingTiles } = action.payload
      const layer = state.visState.layers.find((l) => l.id === layerId)

      if (layer) {
        layer.isLoadingTiles = isLoadingTiles
      }

      return {
        ...state,
        visState: {
          ...state.visState,
          layers: [...state.visState.layers]
        }
      }
    },
    [updateTilesetDatasetTilejson.type]: (
      state: typeof UnconnectedKeplerGlProps,
      action: PayloadAction<{
        datasetId: string
        tilejson?: TilejsonResult | null
        schema?: SchemaField[] | null
      }>
    ) => {
      const { datasetId, tilejson, schema } = action.payload
      const dataset = state.visState.datasets[datasetId]

      if (dataset) {
        setTilejsonFromTilesetDataset(dataset, tilejson)
        if (schema !== undefined) {
          setSchemaFromTilesetDataset(dataset, schema ?? undefined)
        }
      }
      return updateVisStateDatasets(state, datasetId)
    },
    [updateTilesetDatasetResolution.type]: (
      state: typeof UnconnectedKeplerGlProps,
      action: PayloadAction<{
        datasetId: string
        aggregationResLevel: number
      }>
    ) => {
      const { datasetId, aggregationResLevel } = action.payload
      const dataset = state.visState.datasets[datasetId]

      if (dataset) {
        setAggregationResLevelFromTilesetDataset(dataset, aggregationResLevel)
      }
      return updateVisStateDatasets(state, datasetId)
    },
    [reorderLayerWithMove.type]: (
      state: typeof UnconnectedKeplerGlProps,
      action: PayloadAction<{
        layerId: string
        layerMove: LayerMove
      }>
    ) => {
      const layers: KeplerLayer[] = state.visState.layers
      const layerOrder: number[] = state.visState.layerOrder
      const { layerId, layerMove } = action.payload

      return {
        ...state,
        visState: {
          ...state.visState,
          layerOrder: getLayerOrderWithMove(layers, layerOrder, layerId, layerMove)
        }
      }
    }
  })

function updateVisStateDatasets(state: typeof UnconnectedKeplerGlProps, datasetId: string, updatedFilter?: Filter) {
  const newVisState = {
    ...state.visState,
    datasets: {
      // force re-render all selectors that depend on datasets
      ...state.visState.datasets
    }
  }

  return {
    ...state,
    visState: updateAllLayerDomainData(newVisState, datasetId, updatedFilter)
  }
}

export function getTilesetLayerClasses(): KeplerLayerClasses {
  return {
    [LayerTypes.Unknown]: UnknownLayer,
    [LayerTypes.Tileset]: TileLayer,
    [LayerTypes.Quadbin]: QuadbinLayer,
    [LayerTypes.H3]: H3Layer,
    [LayerTypes.HeatmapTile]: HeatmapTileLayer,
    [LayerTypes.ClusterTile]: ClusterTileLayer,
    [LayerTypes.Raster]: RasterTileLayer,

    // layerClasses.mvt is deprecated. We keep it for old maps
    mvt: TileLayer
  } as unknown as KeplerLayerClasses
}

export function getLayerClasses(): KeplerLayerClasses {
  return getTilesetLayerClasses()
}

export const getOpenLayerConfig = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.layers,
  (layers) => {
    if (layers && layers.length > 0) {
      return layers.find((l: Layer) => l.config.isConfigActive)
    }
  }
)

export const keplerIsLoaded = createSelector(
  (state: RootState) => state.keplerGl,
  (keplerGl) => {
    return Object.prototype.hasOwnProperty.call(keplerGl, 'map')
  }
)

export const getFiltersByDatasetId = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.filters,
  (_: unknown, dataId: string) => dataId,
  (filters, dataId) => {
    return filters.filter((f: { dataId: string | string[] }) => f.dataId.indexOf(dataId) >= 0)
  }
)

export const keplerLayersSelector = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.layers,
  (layers) => layers
)
export const getLayers = keplerLayersSelector

export const keplerLayersConfigsSelector = createSelector([keplerLayersSelector], (layers) => {
  return layers.reduce((acc, layer) => {
    acc[layer.id] = layer.config
    return acc
  }, {} as Record<string, KeplerLayerConfig>)
})

export const keplerDatasetsSelector = (state: RootState) => state.keplerGl.map?.visState?.datasets
const datasets = (state: RootState) => state.maps.currentMapDatasets

export const getkeplerDatasetById = createSelector(
  keplerDatasetsSelector,
  (_: unknown, datasetId: string) => datasetId,
  (datasets, datasetId) => {
    return datasets && datasetId in datasets ? datasets[datasetId] : null
  }
)

export const getDatasetZoomRange = createSelector(
  keplerDatasetsSelector,
  (_: unknown, datasetId: string) => datasetId,
  (datasets, datasetId) => {
    return datasetId in datasets ? getZoomRangeFromTilesetDataset(datasets[datasetId]) : null
  }
)

export const getEnrichedDatasets = createSelector([keplerDatasetsSelector, datasets], (keplerDatasets, datasets) => {
  return Object.keys(keplerDatasets).map((datasetId) => ({
    ...keplerDatasets[datasetId],
    ...datasets.find((d) => d.id === datasetId)
  }))
})

export const getEnrichedDatasetsByDatasetIds = createSelector(
  [getEnrichedDatasets, (_: unknown, datasetIds: string[]) => datasetIds],
  (enrichedDatasets, datasetIds) => {
    return enrichedDatasets.filter((ds) => datasetIds.includes(ds.id))
  }
)

export const getEnrichedDatasetById = createEntitySelector(
  [keplerDatasetsSelector, datasets, (_: unknown, datasetId: string) => datasetId],
  (keplerDatasets, datasets, datasetId) => {
    const dataset = datasets.find((d) => d.id === datasetId)
    return datasetId in keplerDatasets ? { ...dataset, ...keplerDatasets[datasetId] } : null
  }
)

export const getDatasetLoadingProgress = createSelector([datasets], (datasets) => {
  const initialPercentage = 10
  if (!datasets.length) return 100
  const n = datasets.filter((d) => d.status === RequestStatus.Fulfilled || d.status === RequestStatus.Rejected).length
  return Math.max(Math.round((n / datasets.length) * 100), initialPercentage)
})

export const getLayerByDatasetId = createEntitySelector(
  keplerLayersSelector,
  (_: unknown, datasetId: string) => datasetId,
  (layers, datasetId): Layer => {
    return layers.find((layer: Layer) => layer.config.dataId === datasetId)
  }
)

export const datasetHasValidLayerSelector = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.layers,
  (_: unknown, datasetId: string) => datasetId,
  (layers, datasetId): boolean => {
    return layers.some((layer: Layer) => layer.config.dataId === datasetId && layer.type !== LayerTypes.Unknown)
  }
)

export const getLayersByDatasetId = createEntitySelector(
  keplerLayersSelector,
  (_: unknown, datasetId: string) => datasetId,
  (layers, datasetId): Layer[] => {
    return layers.filter((layer: Layer) => layer.config.dataId === datasetId)
  }
)

export const getLayerToBeMergedByDatasetId = createEntitySelector(
  (state: RootState) => state.keplerGl?.map?.visState.layerToBeMerged,
  (_: unknown, datasetId: string) => datasetId,
  (layers, datasetId): Layer => {
    return layers.find((layer: Layer) => layer.config.dataId === datasetId)
  }
)

export const getLayerById = createEntitySelector(
  keplerLayersSelector,
  (_: unknown, layerId: string) => layerId,
  (layers, layerId): Layer => {
    return layers.find((layer: Layer) => layer.id === layerId)
  }
)

export const getDatasetsByType = createSelector(
  keplerDatasetsSelector,
  (_: unknown, type: MapType) => type,
  (datasets, type): KeplerTable[] => Object.values(datasets as KeplerTable[]).filter((d) => d.metadata?.type === type)
)

export const isDatasetLoadingTiles = createEntitySelector(
  keplerLayersSelector,
  (_: unknown, datasetId: string) => datasetId,
  (layers, datasetId: string): boolean =>
    layers.filter((layer) => layer.config.dataId === datasetId).some((layer) => layer.isLoadingTiles)
)

export const keplerSplitMapsSelector = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState?.splitMaps,
  (splitMaps) => splitMaps
)

export const isMapSplit = createSelector(keplerSplitMapsSelector, (splitMaps) => splitMaps && splitMaps.length > 1)

export const keplerLayersToRenderSelector = createEntitySelector(
  [keplerLayersSelector, keplerSplitMapsSelector, (_: RootState, index: number) => index],
  (layers: Layer[], splitMaps, index: number) => {
    if (!layers) return {}

    const layerIndicesToRender: number[] = layers
      .map((layer, idx) => (splitMaps?.length ? (splitMaps[index]?.layers?.[layer.id] ? idx : null) : idx))
      .filter((idx) => idx !== null)

    return layerIndicesToRender.reduce((acc, layerIdx) => {
      const layer = layers[layerIdx]
      if (layer.config.isVisible) {
        acc[layer.id] = true
      }
      return acc
    }, {} as Record<string, boolean>)
  }
)

export const keplerLayerOrderSelector = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState?.layerOrder,
  (layerOrder: number[]) => layerOrder
)

const activeSidePanel = (state: RootState) => state.keplerGl?.map?.uiState.activeSidePanel

export const panelLayerActive = createSelector(
  [activeSidePanel],
  (activeSidePanel) => activeSidePanel === LAYER_SIDE_PANEL
)

export default keplerGlReducer
